import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import GavelIcon from '@mui/icons-material/Gavel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CommitIcon from '@mui/icons-material/Commit';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AssigneePicker from './AssigneePicker';
import { HILL_COLORS, zoneColor } from './hillGeometry';
import { prChipStyle, prStateSummary, commitSummary } from './prPresentation';
import { linkPullRequest, unlinkPullRequest } from '../../utils/githubApi';
import { timeAgo } from '../../utils/relativeTime';

// How many commits to show before the "N more" expander kicks in.
const COMMIT_PREVIEW_COUNT = 5;

// A shared label style for the Development subsection headers.
const devLabelSx = {
  fontWeight: 700,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const statusColor = (scope) => {
  if (scope.isParked) return HILL_COLORS.notStarted;
  if (typeof scope.position === 'number') return zoneColor(scope.position);
  return HILL_COLORS.notStarted;
};

/**
 * When someone last placed this dot, in the shortest phrase that stays honest.
 */
const judgedLabel = (scope, manual = false) => {
  if (!scope.lastJudgedAt) return manual ? 'position from the status' : 'position from the Jira status';
  const days = scope.judgedDaysAgo;
  if (days === 0 || days === null || days === undefined) return 'placed today';
  if (days === 1) return 'placed yesterday';
  return `placed ${days} days ago`;
};

/**
 * A scope's identity, status, and the calls a lead can make about it, laid out
 * vertically for a narrow drawer. This is the shared source of truth behind the
 * drawer's Details tab; the comment thread lives in its own tab, not here.
 */
const ScopeDetailContent = ({
  scope,
  onClearOverride,
  onTogglePark,
  onExclude,
  onRename,
  onHammer,
  onRestate,
  onChangeStatus,
  onChangeAssignee,
  onScopeChanged,
  manual = false,
  statusOptions = [],
  readOnly = false,
  members = [],
  onChangeAssigneeMember,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingAssignee, setEditingAssignee] = useState(false);
  const [assigneeDraft, setAssigneeDraft] = useState('');
  const [prUrl, setPrUrl] = useState('');
  const [linkingPr, setLinkingPr] = useState(false);
  const [prError, setPrError] = useState(null);
  const [commitsExpanded, setCommitsExpanded] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // Selecting a different dot should never carry the previous edit along.
  useEffect(() => {
    setEditing(false);
    setEditingAssignee(false);
    setPrUrl('');
    setPrError(null);
    setCommitsExpanded(false);
    setCodeCopied(false);
  }, [scope?.id]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(scope.code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1500);
    } catch (err) {
      // Clipboard can be blocked (insecure context, denied permission); the code
      // is still visible to copy by hand, so there is nothing to recover from.
    }
  };

  if (!scope) return null;

  const color = statusColor(scope);
  const isOverridden = scope.positionSource === 'override';
  const canRename = Boolean(onRename) && !readOnly;

  const startEditing = () => {
    setDraft(scope.jiraSummary || '');
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    onRename(scope, draft);
  };

  const startEditingAssignee = () => {
    setAssigneeDraft(scope.assigneeName || '');
    setEditingAssignee(true);
  };

  const commitAssignee = () => {
    setEditingAssignee(false);
    onChangeAssignee(scope, assigneeDraft);
  };

  const pullRequests = scope.pullRequests || [];
  const branches = scope.branches || [];
  // Newest commits first, so the freshest work is at the top of the list.
  const commits = [...(scope.commits || [])].sort((a, b) => {
    const at = a.committedAt ? new Date(a.committedAt).getTime() : 0;
    const bt = b.committedAt ? new Date(b.committedAt).getTime() : 0;
    return bt - at;
  });
  const visibleCommits = commitsExpanded ? commits : commits.slice(0, COMMIT_PREVIEW_COUNT);
  const jiraKeyLabel = scope.jiraKey || scope.code || 'this scope';

  const handleLinkPr = async () => {
    const url = prUrl.trim();
    if (!url) return;
    setLinkingPr(true);
    setPrError(null);
    try {
      await linkPullRequest(scope.id, url);
      setPrUrl('');
      // Refetch so the new link flows back in with the rest of the scope's data.
      if (onScopeChanged) await onScopeChanged();
    } catch (err) {
      setPrError(err.response?.data?.message || 'Could not link that pull request');
    } finally {
      setLinkingPr(false);
    }
  };

  const handleUnlinkPr = async (pr) => {
    try {
      await unlinkPullRequest(scope.id, pr.id);
      if (onScopeChanged) await onScopeChanged();
    } catch (err) {
      setPrError(err.response?.data?.message || 'Could not unlink that pull request');
    }
  };

  return (
    <Box>
      {editing ? (
        <TextField
          size="small"
          fullWidth
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={commit} aria-label="Save scope name">
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setEditing(false)} aria-label="Cancel rename">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {scope.jiraSummary || scope.jiraKey}
          </Typography>
          {canRename && (
            <Tooltip title={manual ? 'Rename this scope' : 'Rename this issue in Jira'}>
              <IconButton size="small" onClick={startEditing} aria-label="Rename scope">
                <EditOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
        {manual && !readOnly && onChangeStatus ? (
          <Select
            size="small"
            value={statusOptions.includes(scope.jiraStatus) ? scope.jiraStatus : ''}
            displayEmpty
            onChange={(e) => onChangeStatus(scope, e.target.value)}
            sx={{
              '& .MuiSelect-select': { py: 0.5, fontWeight: 600 },
              bgcolor: color,
              color: '#0a0a0a',
              borderRadius: 4,
            }}
          >
            {!statusOptions.includes(scope.jiraStatus) && (
              <MenuItem value="" disabled>{scope.jiraStatus || 'Set a status'}</MenuItem>
            )}
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>
                {status}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Chip
            size="small"
            label={scope.jiraStatus || 'Unknown'}
            sx={{ bgcolor: color, color: '#0a0a0a', fontWeight: 600, textTransform: 'capitalize' }}
          />
        )}

        {/* The Jira issue type this scope came in as, so a board mixing types (a
            custom "Scope" alongside Stories) reads at a glance. */}
        {scope.jiraIssueType && (
          <Chip size="small" variant="outlined" label={scope.jiraIssueType} />
        )}

        {/* The app-generated scope code. Devs drop it in a branch name or commit
            message (e.g. feature/SCP-F2A4-thing) to auto-link GitHub work to this
            scope without needing a Jira key. */}
        {scope.code && (manual || !scope.jiraKey) && (
          <Tooltip title={codeCopied ? 'Copied' : 'Put it in a branch name to auto-link'}>
            <Chip
              size="small"
              variant="outlined"
              icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              label={scope.code}
              onClick={copyCode}
              sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
            />
          </Tooltip>
        )}

        {isOverridden && (
          <Chip
            size="small"
            variant="outlined"
            label={`Set by hand at ${Math.round((scope.position || 0) * 100)}%`}
          />
        )}

        {/* A guessed position is the one case where the chart is asserting something
            nobody said, so it admits it rather than blending in with the rest. */}
        {scope.isProvisional && (
          <Tooltip
            title={`"${scope.jiraStatus}" is not in the hill map, so this dot came from Jira's status
              category and is held below the peak. Map the status in settings, or drag the dot.`}
          >
            <Chip
              size="small"
              variant="outlined"
              color="default"
              icon={<HelpOutlineIcon sx={{ fontSize: 14 }} />}
              label="Position guessed"
            />
          </Tooltip>
        )}

        {/* A stalled dot usually means unresolved unknowns rather than idleness,
            so the wording invites a conversation instead of implying blame. */}
        {scope.isStuck && (
          <Tooltip title="Worth asking what is in the way. A dot that stays put is usually a sign of unknowns nobody has raised yet.">
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />}
              label={`Hasn't moved in ${scope.stillForDays} days`}
            />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ mt: 1.5 }}>
        {/* When the bet has a team, ownership is picked from it and persisted
            (to Jira for a Jira scope, locally for a manual one). Falls back to
            the free-text field for a manual scope with no team, and to a plain
            read-only name otherwise. */}
        {onChangeAssigneeMember && members.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssigneePicker
              target={scope}
              members={members}
              onAssign={readOnly ? undefined : (userId) => onChangeAssigneeMember(scope, userId)}
              size={26}
            />
            <Typography variant="body2" color="text.secondary">
              {scope.assigneeName || 'Unassigned'}
            </Typography>
          </Box>
        ) : manual && !readOnly && onChangeAssignee ? (
          editingAssignee ? (
            <TextField
              size="small"
              autoFocus
              placeholder="Assignee"
              value={assigneeDraft}
              onChange={(e) => setAssigneeDraft(e.target.value)}
              onBlur={commitAssignee}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitAssignee();
                if (e.key === 'Escape') setEditingAssignee(false);
              }}
              sx={{ width: 160 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <Button
              size="small"
              color="inherit"
              startIcon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={startEditingAssignee}
              sx={{ textTransform: 'none', color: scope.assigneeName ? 'text.secondary' : 'text.disabled' }}
            >
              {scope.assigneeName || 'Assign'}
            </Button>
          )
        ) : (
          scope.assigneeName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">{scope.assigneeName}</Typography>
            </Box>
          )
        )}
      </Box>

      {!readOnly && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }} useFlexGap>
          {onRestate && (
            <Tooltip title="Say where this really is without moving it, and why. Counts as a fresh read of the unknowns.">
              <Button size="small" startIcon={<RateReviewOutlinedIcon sx={{ fontSize: 15 }} />} onClick={() => onRestate(scope)}>
                Still here
              </Button>
            </Tooltip>
          )}

          {isOverridden && (
            <Tooltip title={manual ? 'Let the status decide this position again' : 'Let the Jira status decide this position again'}>
              <Button size="small" startIcon={<RestartAltIcon />} onClick={() => onClearOverride(scope)}>
                {manual ? 'Follow status' : 'Follow Jira'}
              </Button>
            </Tooltip>
          )}

          <Button size="small" onClick={() => onTogglePark(scope)}>
            {scope.isParked ? 'Unpark' : 'Park'}
          </Button>

          {onHammer && (
            <Tooltip
              title={scope.isHammered
                ? 'Put this scope back in the bet'
                : 'Cut this scope to fit the appetite. It leaves the hill and is counted on the cycle scoreboard.'}
            >
              <Button
                size="small"
                color={scope.isHammered ? 'warning' : 'inherit'}
                startIcon={<GavelIcon sx={{ fontSize: 15 }} />}
                onClick={() => onHammer(scope)}
              >
                {scope.isHammered ? 'Unhammer' : 'Hammer'}
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Hide this scope from the hill without touching Jira">
            <Button size="small" color="inherit" onClick={() => onExclude(scope)}>
              Remove
            </Button>
          </Tooltip>
        </Stack>
      )}

      {/* The reason behind the position, which is the part that survives the cycle.
          Its absence is information too: a dot nobody has placed is following a
          Jira workflow, not reporting anyone's read of the unknowns. */}
      {(scope.lastJudgmentNote || scope.lastJudgedAt) ? (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 1.5, flexWrap: 'wrap' }}>
          {scope.lastJudgmentNote && (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              “{scope.lastJudgmentNote}”
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled">
            {judgedLabel(scope, manual)}
          </Typography>
        </Box>
      ) : (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
          {manual
            ? 'Nobody has placed this dot. It is wherever the status puts it.'
            : 'Nobody has placed this dot. It is wherever the Jira status puts it.'}
        </Typography>
      )}

      {/* The Development panel: branches, commits, and pull requests linked to
          this scope, all auto-matched by the Jira key in a branch name, commit
          message, or PR (PRs can also be added by hand below). */}
      <Box sx={{ mt: 2.5 }}>
        <Typography variant="caption" sx={{ ...devLabelSx, display: 'block', mb: 1.5 }}>
          Development
        </Typography>

        {/* Branches */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <AccountTreeOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" sx={devLabelSx}>
              Branches
            </Typography>
          </Box>
          {branches.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              No branches reference {jiraKeyLabel} yet.
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {branches.map((branch) => (
                <Box key={branch.id} sx={{ minWidth: 0 }}>
                  <Link
                    href={branch.url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, maxWidth: '100%' }}
                    noWrap
                  >
                    <Typography variant="body2" noWrap sx={{ maxWidth: 260, fontFamily: 'monospace' }}>
                      {branch.name}
                    </Typography>
                    <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </Link>
                  {branch.lastActivityAt && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                      updated {timeAgo(branch.lastActivityAt)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* Commits */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <CommitIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" sx={devLabelSx}>
              Commits
            </Typography>
          </Box>
          {commits.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              No commits reference {jiraKeyLabel} yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {visibleCommits.map((commit) => {
                const { shortSha, message } = commitSummary(commit);
                return (
                  <Box key={commit.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={shortSha}
                      component={commit.url ? 'a' : 'div'}
                      href={commit.url || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable={Boolean(commit.url)}
                      sx={{ flexShrink: 0, fontFamily: 'monospace', height: 20 }}
                    />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" noWrap title={message}>
                        {message}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                        {(commit.authorLogin || commit.authorName)
                          ? `${commit.authorLogin || commit.authorName} · `
                          : ''}
                        {timeAgo(commit.committedAt) || ''}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              {commits.length > COMMIT_PREVIEW_COUNT && (
                <Button
                  size="small"
                  onClick={() => setCommitsExpanded((v) => !v)}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                >
                  {commitsExpanded
                    ? 'Show fewer'
                    : `${commits.length - COMMIT_PREVIEW_COUNT} more`}
                </Button>
              )}
            </Stack>
          )}
        </Box>

        {/* Pull requests */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <CallSplitIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" sx={devLabelSx}>
            Pull requests
          </Typography>
        </Box>

        {pullRequests.length === 0 ? (
          <Typography variant="body2" color="text.disabled">
            None linked yet. A PR whose branch or title names {scope.jiraKey || scope.code || 'this scope'} links here automatically.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {pullRequests.map((pr) => {
              const { color, label } = prChipStyle(pr);
              return (
                <Box
                  key={pr.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                >
                  <Chip size="small" variant="outlined" color={color} label={label} sx={{ flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Link
                      href={pr.url || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, maxWidth: '100%' }}
                      noWrap
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                        #{pr.number} {pr.title || pr.repoFullName}
                      </Typography>
                      <OpenInNewIcon sx={{ fontSize: 12 }} />
                    </Link>
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                      {prStateSummary(pr)}
                      {pr.authorLogin ? ` · ${pr.authorLogin}` : ''}
                    </Typography>
                  </Box>
                  {!readOnly && (
                    <Tooltip title="Unlink this pull request">
                      <IconButton size="small" onClick={() => handleUnlinkPr(pr)} aria-label="Unlink pull request">
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {!readOnly && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Paste a GitHub PR URL to link"
              value={prUrl}
              onChange={(e) => setPrUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleLinkPr();
              }}
              error={Boolean(prError)}
              helperText={prError || ''}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={handleLinkPr}
              disabled={linkingPr || !prUrl.trim()}
              sx={{ flexShrink: 0, mt: 0.25 }}
            >
              {linkingPr ? 'Linking…' : 'Link'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ScopeDetailContent;
